import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, apiUrl, resolveMediaUrl } from '@/lib/api';
import { getSessionExpiredLoginUrl } from '@/lib/loginPath';
import { useSession } from '@/hooks/useSession';
import { Button } from '@/components/ui/Button';
import { mobileAdmin } from '@/lib/mobileAdminPaths';
import { cn } from '@/lib/cn';

const MANUAL_ENTRY_IMAGE_MAX_BYTES = 1024 * 1024;

const fieldClass =
  'w-full rounded-2xl border border-slate-200/90 bg-white px-4 py-3.5 text-base text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-teal-500/60 focus:outline-none focus:ring-2 focus:ring-teal-500/25';
const labelClass = 'mb-2 block text-sm font-semibold text-slate-700';

export function MobileRegisterPage() {
  const navigate = useNavigate();
  const { currentSiteId } = useSession();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [phone, setPhone] = useState('');
  const [imagePaths, setImagePaths] = useState<string[]>([]);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function uploadImages(files: FileList | null) {
    if (!files?.length) return;
    if (currentSiteId < 1) {
      setErr('请先在上方选择工作站点');
      return;
    }
    setUploadBusy(true);
    setErr(null);
    const next: string[] = [];
    try {
      for (const file of Array.from(files)) {
        if (file.size > MANUAL_ENTRY_IMAGE_MAX_BYTES) {
          throw new Error(`「${file.name}」超过单张 1MB，请先压缩`);
        }
        const fd = new FormData();
        fd.append('file', file);
        fd.append('site_id', String(currentSiteId));
        const res = await fetch(apiUrl('upload_manual_entry_image'), { method: 'POST', body: fd, credentials: 'include' });
        const raw = await res.text();
        const text = raw.replace(/^\uFEFF/, '').trim();
        let parsed: { ok?: boolean; err?: string; local_path?: string };
        try {
          parsed = JSON.parse(text) as { ok?: boolean; err?: string; local_path?: string };
        } catch {
          throw new Error(text.slice(0, 120) || '上传响应无效');
        }
        if (parsed.err === 'unauthorized' && typeof window !== 'undefined') {
          window.location.replace(getSessionExpiredLoginUrl());
          return;
        }
        if (!parsed.ok || !parsed.local_path) {
          const e = parsed.err || '上传失败';
          throw new Error(e === 'file_too_large' ? '单张图片超过 1MB' : e);
        }
        next.push(parsed.local_path);
      }
      setImagePaths((prev) => [...prev, ...next]);
    } catch (e) {
      setErr(e instanceof Error ? e.message : '上传失败');
    } finally {
      setUploadBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  function removeImage(path: string) {
    setImagePaths((prev) => prev.filter((p) => p !== path));
  }

  async function submit() {
    const t = title.trim();
    const b = body.trim();
    if (t === '' && b === '') {
      setErr('请填写标题或正文');
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      const r = await apiFetch<{ ok: boolean; err?: string; detail?: string; entry_id?: number }>('create_manual_entry', {
        method: 'POST',
        body: JSON.stringify({
          title_text: t,
          body_text: b,
          contact_phone: phone.trim(),
          image_local_paths: imagePaths.length > 0 ? imagePaths : undefined,
        }),
      });
      if (r.ok) {
        navigate(mobileAdmin.entries, {
          replace: true,
          state: {
            flash: '登记已保存。在列表中点开该条，可「上传公众号草稿箱」。',
          },
        });
      } else {
        setErr(r.detail || r.err || '保存失败');
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : '保存失败');
    } finally {
      setSaving(false);
    }
  }

  if (currentSiteId < 1) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-bold text-slate-900">登记信息</h1>
        <p className="text-base text-slate-600">请先在上方「当前工作站点」中选择站点，再填写要发布的内容。</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-slate-900">登记信息</h1>
        <p className="mt-2 text-base leading-relaxed text-slate-600">
          出门在外可直接填写标题与正文；复杂排版、字段映射等请在电脑端配置。
        </p>
      </div>

      {err ? (
        <p className="rounded-2xl border border-rose-200/90 bg-rose-50 px-4 py-3 text-base font-medium text-rose-900">
          {err}
        </p>
      ) : null}

      <div className="space-y-5 rounded-3xl border border-slate-200/80 bg-white/90 p-5 shadow-sm">
        <label className="block">
          <span className={labelClass}>标题（可与正文二选一或都填）</span>
          <input
            className={fieldClass}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="简短标题"
            autoComplete="off"
          />
        </label>

        <label className="block">
          <span className={labelClass}>正文</span>
          <textarea
            className={cn(fieldClass, 'min-h-[10rem] resize-y leading-relaxed')}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="要发布的主要内容"
          />
        </label>

        <label className="block">
          <span className={labelClass}>联系电话（可选）</span>
          <input
            className={fieldClass}
            type="tel"
            inputMode="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="选填"
            autoComplete="tel"
          />
        </label>

        <div>
          <span className={labelClass}>配图（可选，单张 ≤1MB，可多选）</span>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => void uploadImages(e.target.files)}
          />
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <Button
              type="button"
              variant="secondary"
              className="min-h-12 w-full text-base sm:w-auto"
              disabled={uploadBusy}
              onClick={() => fileInputRef.current?.click()}
            >
              {uploadBusy ? '上传中…' : '选择图片'}
            </Button>
          </div>
          {imagePaths.length > 0 ? (
            <ul className="mt-3 space-y-2">
              {imagePaths.map((p) => (
                <li
                  key={p}
                  className="flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50/80 px-3 py-2"
                >
                  <img
                    src={resolveMediaUrl(p)}
                    alt=""
                    className="h-14 w-14 shrink-0 rounded-lg object-cover"
                  />
                  <span className="min-w-0 flex-1 truncate font-mono text-xs text-slate-600">{p}</span>
                  <button
                    type="button"
                    className="shrink-0 rounded-lg px-3 py-2 text-sm font-semibold text-rose-700 active:bg-rose-50"
                    onClick={() => removeImage(p)}
                  >
                    移除
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </div>

      <Button
        type="button"
        variant="primary"
        className="min-h-14 w-full text-lg font-semibold shadow-lg shadow-teal-900/15"
        disabled={saving || uploadBusy}
        onClick={() => void submit()}
      >
        {saving ? '保存中…' : '保存登记'}
      </Button>

      <p className="text-center text-sm text-slate-500">
        保存后请到「信息查询」中打开该条，使用「上传公众号草稿箱」。
      </p>
    </div>
  );
}
